import _get from 'lodash/get';
import config from '@bedrock-rdk/config';
import { getSvgTexture, iconByName } from '@tornado-rdk/icons';
import { getFullPath } from '../../settings/app.settings';
import { ICON_TYPE, ICON_COLOR_SUFFIX } from './icon.constants';

const textureCache = {};

/**
 * Ceiling on cached icon textures. The cache is keyed by url plus view box plus scale, so a long session across
 * many services and pictos keeps adding entries that are never asked for again. Only the Texture objects are held
 * here — the underlying sources stay owned by Lightning's TextureManager, so evicting an entry costs a Texture
 * re-creation and nothing more.
 */
const MAX_CACHED_TEXTURES = 160;

const cacheTexture = (id, texture) => {
  textureCache[id] = texture;

  const keys = Object.keys(textureCache);

  // String keys keep insertion order, so the front of the list is the oldest entry
  if (keys.length > MAX_CACHED_TEXTURES) {
    delete textureCache[keys[0]];
  }
};

const getServiceIconFromBundle = (name, colorMode = 'default') => {
  const baseUrl = _get(config, 'assetsBundle.manifest.baseUrl');
  const icon = _get(config, `assetsBundle.manifest.urls.viewbox.${name}${ICON_COLOR_SUFFIX[colorMode]}`);

  if (!baseUrl) {
    return null;
  }
  if (!icon) {
    return null;
  }

  return `${baseUrl}/${icon}`;
};

const getServiceIcon = (name, colorMode = 'default') => {
  const fullCode = getServiceIconFromBundle(name, colorMode);

  return fullCode;
};

export const getIconUrl = (type, name, collection, colorMode) => {
  switch (type) {
    case ICON_TYPE.SERVICE_ICON:
      return getServiceIcon(name, colorMode);

    default:
      return getFullPath(iconByName(name.toLowerCase(), collection));
  }
};

export const getTextureByUrl = (url, viewBox, scale, useCache = true) => {
  const { w: width, h: height } = viewBox;
  const id = [url, width, height, scale].join('_');
  if (useCache && textureCache[id]) {
    return textureCache[id];
  }
  const result = getSvgTexture(url, width, height, scale);
  if (useCache) {
    cacheTexture(id, result);
  }

  return result;
};

export const getTextureByName = (picto, viewBox, scale = 1, colorMode, useCache = true) => {
  if (!picto || !picto.name || !viewBox) {
    return null;
  }

  const { name, type, collection } = picto;
  const url = getIconUrl(type, name, collection, colorMode);

  return getTextureByUrl(url, viewBox, scale, useCache);
};

export const getTextureByIconData = iconData => {
  if (iconData) {
    const { picto, viewBox, colorMode } = iconData;
    if (picto && viewBox) {
      return getTextureByName(picto, viewBox, iconData.scale || 1, colorMode);
    }
  }

  return null;
};

export const cleanupIconsCache = () => {
  Object.keys(textureCache).forEach(key => {
    delete textureCache[key];
  });
};
